import { breakpoints } from './tokens/breakpoints';
import { colors } from './tokens/colors';
import { containers } from './tokens/containers';
import { density, type DensityMode } from './tokens/density';
import { iconSizes } from './tokens/icons';
import { motion } from './tokens/motion';
import { opacity } from './tokens/opacity';
import { r, radii } from './tokens/radii';
import { shadows } from './tokens/shadows';
import { sizes } from './tokens/sizes';
import { s, spacing, spacingBase, spacingScale } from './tokens/spacing';
import { fontFamilies, typography } from './tokens/typography';
import { zIndex } from './tokens/zIndex';
import type { TypographyProps } from '@mui/material';

export const colorTokenModes = {
  light: {
    green: colors.light.success,
    red: colors.light.danger,
    orange: colors.light.warning,
    brown: colors.light.brown,
    purple: colors.light.purple,
    blue: colors.light.info,
    teal: colors.light.primary,
    pink: colors.light.pink,
    neutral: colors.light.neutral,
  },
  dark: {
    green: colors.dark.success,
    red: colors.dark.danger,
    orange: colors.dark.warning,
    brown: colors.dark.brown,
    purple: colors.dark.purple,
    blue: colors.dark.info,
    teal: colors.dark.primary,
    pink: colors.dark.pink,
    neutral: colors.dark.neutral,
  },
} as const;

export type ColorTokenMode = keyof typeof colorTokenModes;
export type ColorTokens = typeof colorTokenModes.light | typeof colorTokenModes.dark;

export const getColorTokens = (mode: ColorTokenMode): ColorTokens => colorTokenModes[mode];

export const colorTokens = colorTokenModes.light;

export const surfaceTokenModes = {
  light: {
    page: '#f7f9fc',
    pageAlt: '#fafbfd',
    pageMuted: '#f1f3f6',
    paper: '#ffffff',
    border: '#e2e8f0',
  },
  dark: {
    page: '#181818',
    pageAlt: '#1e1e1e',
    pageMuted: '#242424',
    paper: '#202020',
    border: '#3a3a3a',
  },
} as const;

export type SurfaceTokenMode = keyof typeof surfaceTokenModes;
export type SurfaceTokens = typeof surfaceTokenModes.light | typeof surfaceTokenModes.dark;

export const getSurfaceTokens = (mode: SurfaceTokenMode): SurfaceTokens => surfaceTokenModes[mode];

export const surfaceTokens = surfaceTokenModes.light;

export const designTokens = {
  radii: { card: radii.card, popover: radii.md, chip: radii.pill },
  elevation: {
    card: '0px 16px 40px rgba(15, 23, 42, 0.08)',
    overlay: '0px 18px 46px rgba(15, 23, 42, 0.12)',
  },
  spacing: { scale: spacingScale },
  gradients: { page: surfaceTokens.page },
};

export type DesignTokens = typeof designTokens;

export type SpacingToken = keyof typeof designTokens.spacing.scale;

export const tableTokens = {
  cellPadding: { x: 'xs', y: 'xxs' },
  rowHeight: 36,
  headerHeight: 36,
} as const satisfies {
  cellPadding: { x: SpacingToken; y: SpacingToken };
  rowHeight: number;
  headerHeight: number;
};

export type TypographyToken = {
  variant:
    | 'inherit'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'button'
    | 'overline';
  component?: TypographyProps['component'];
  fontWeight?: number;
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing?: string;
  fontSize?: string;
};

export const typographyTokens = {
  pageTitle: {
    variant: 'h1',
    fontWeight: 400,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: undefined,
  },
  sectionTitle: {
    variant: 'h4',
    component: 'h2',
    fontWeight: 400,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: undefined,
  },
  brand: {
    variant: 'h4',
    component: 'span',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: undefined,
  },
  overline: {
    variant: 'overline',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: undefined,
  },
  chip: {
    variant: 'caption',
    fontWeight: 400,
    textTransform: 'uppercase',
    letterSpacing: undefined,
    fontSize: undefined,
  },
  body: {
    variant: 'body1',
    fontWeight: 400,
    textTransform: undefined,
    letterSpacing: undefined,
    fontSize: undefined,
  },
  bodyMuted: {
    variant: 'body1',
    fontWeight: 400,
    textTransform: undefined,
    letterSpacing: undefined,
    fontSize: undefined,
  },
  bodyStrong: {
    variant: 'subtitle1',
    fontWeight: 600,
    textTransform: undefined,
    letterSpacing: undefined,
    fontSize: undefined,
  },
  caption: {
    variant: 'caption',
    fontWeight: 400,
    textTransform: undefined,
    letterSpacing: undefined,
    fontSize: undefined,
  },
  captionSmall: {
    variant: 'caption',
    fontWeight: 400,
    textTransform: undefined,
    letterSpacing: undefined,
    fontSize: '12px',
  },
  label: {
    variant: 'overline',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: undefined,
  },
} as const satisfies Record<string, TypographyToken>;

export type TypographyRole = keyof typeof typographyTokens;

export const radiusTokens = {
  none: radii.none,
  sm: radii.sm,
  md: radii.md,
  card: radii.card,
} as const;

export type RadiusToken = keyof typeof radiusTokens;

export const elevationTokens = {
  none: 'none',
  card: designTokens.elevation.card,
  modal: designTokens.elevation.overlay,
} as const;

export type ElevationToken = keyof typeof elevationTokens;

export const motionTokens = {
  duration: {
    instant: '0ms',
    fast: '120ms',
    base: '180ms',
    slow: '240ms',
    slower: '320ms',
    long: '480ms',
    xlong: '600ms',
    shimmer: '1800ms',
  },
  easing: { standard: 'ease', in: 'ease-in', out: 'ease-out', inOut: 'ease-in-out' },
} as const;

export type MotionTokens = typeof motionTokens;
export type MotionDurationToken = keyof typeof motionTokens.duration;
export type MotionEasingToken = keyof typeof motionTokens.easing;

export { iconSizes };
export { r };
export { s };
export { spacing };
export { spacingBase };
export { spacingScale };
export { breakpoints };
export { colors };
export { containers };
export { density };
export type { DensityMode };
export { motion };
export { opacity };
export { radii };
export { shadows };
export { sizes };
export { typography };
export { fontFamilies };
export { zIndex };
